import { IBaseViews } from "app/based/baseViews/BaseViews";
import LoadingScreenComponent from "components/generalComponents/loadingScreenComponent/LoadingScreenComponent";
import { HigherOrderComponent } from "components/higherOrderComponent/HigherOrderComponent";
import PrivateRoute from "components/layoutComponent/PrivateRoute";
import { FeaturedSideBarModel } from "components/sidebar/data/models/SidebarMenuConfigModel";
import { AuthenticationContext } from "context/AuthenticationContext";
import { focusHandling } from "cruip-js-toolkit";
import { GeneralsInjection } from "features/generals/di/GeneralsInjection";
import GeneralFeature from "features/generals/presentation/views/GeneralFeature";
import { AuthenticationInjection } from "features/login/di/AuthenticationInjection";
import LoginFeature from "features/login/presentation/views/LoginFeature";
import NotFound from "features/notFound/NotFound";
import AppPages from "navigations/AppPages";
import React, {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { DataFormat } from "utils/format/DataFormat";
import { AppCoreInjection } from "utils/serviceRepo/ServiceRepo";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const [diInjected, setDiInjected] = useState<boolean>(false);
  const { menuConfig } = AuthenticationContext();

  const initDependency = () => {
    try {
      AppCoreInjection.initialize();
      setDiInjected(true);
    } catch (error) {
      console.error("Dependency Injection error:", error);
    }
  };

  const mapMenuToRoutes = useCallback(
    (menu: FeaturedSideBarModel) => {
      const Component = AppPages.getPage(menu.componentName);
      const diToken = AppPages.getToken(menu.componentName);
      const injectionModule = AppPages.getInjectionModule(menu.componentName);
      const props = AppPages.getAddtionalProps(menu.componentName);
      const menuKey = `${menu?.name} - ${menu?.componentName} - ${menu?.path}`;

      if (!Component) return <LoadingScreenComponent />;

      return (
        <React.Fragment key={`${menuKey} - routes`}>
          <Route
            path={menu.path}
            key={menuKey}
            element={
              <PrivateRoute path={menu.path} key={menuKey}>
                {!diInjected ? null : (
                  <Suspense fallback={<LoadingScreenComponent />}>
                    <HigherOrderComponent
                      Component={Component}
                      componentProps={{
                        ...(diToken ? { token: diToken } : {}),
                        ...(injectionModule ? { injectionModule } : {}),
                        addtionalProps: {
                          ...(props as IBaseViews),
                          routeParams,
                        },
                      }}
                    />
                  </Suspense>
                )}
              </PrivateRoute>
            }
          />
          <Route
            path={`${menu?.path}/:searchCode`}
            key={`${menuKey} - searchCode`}
            element={
              <PrivateRoute
                path={`${menu?.path}/:searchCode`}
                key={`${menuKey} - searchCode`}
              >
                {!diInjected ? null : (
                  <Suspense fallback={<LoadingScreenComponent />}>
                    <HigherOrderComponent
                      Component={Component}
                      componentProps={{
                        ...(diToken ? { token: diToken } : {}),
                        ...(injectionModule ? { injectionModule } : {}),
                        addtionalProps: {
                          ...(props as IBaseViews),
                          routeParams,
                        },
                      }}
                    />
                  </Suspense>
                )}
              </PrivateRoute>
            }
          />
        </React.Fragment>
      );
    },
    [diInjected, routeParams],
  );

  const hasMenuPath = (
    menus: FeaturedSideBarModel[] = [],
    pathname?: string,
  ): boolean => {
    if (!pathname) {
      return false;
    }

    for (const menu of menus) {
      if (menu?.path === pathname) {
        return true;
      }

      if (menu?.sub?.length && hasMenuPath(menu.sub, pathname)) {
        return true;
      }
    }

    return false;
  };

  const routeRendering = useMemo(() => {
    let routes: Array<ReactNode> = [];
    menuConfig?.menu?.forEach((menu) => {
      routes.push(mapMenuToRoutes(menu));
      if (menu.sub?.length > 0) {
        menu.sub?.forEach((subMenu) => {
          routes.push(mapMenuToRoutes(subMenu));
          if (subMenu.path === "/transaction-history/app-sim-mobifone") {
          }
        });
      }
    });
    return routes;
  }, [menuConfig, mapMenuToRoutes]);

  const defaultMenu = useMemo(() => {
    const menus = menuConfig?.menu || [];
    const allMenus = menus.flatMap((menu) => [menu, ...(menu.sub || [])]);

    return (
      allMenus.find((menu) => menu?.componentName === "GeneralFeature") ||
      allMenus.find((menu) => !!menu?.path)
    );
  }, [menuConfig]);

  const pageRendering = () => {
    const shouldRedirectToDefaultMenu =
      !!DataFormat.getToken() &&
      location?.pathname !== "/" &&
      !!menuConfig &&
      !hasMenuPath(menuConfig?.menu || [], location?.pathname);

    if (typeof window === "undefined" || !diInjected) {
      return <>{<LoadingScreenComponent />}</>;
    }

    return (
      <>
        <Route
          path={"/"}
          key={"default"}
          element={
            <PrivateRoute path={"/"} key={"default"}>
              {!menuConfig ? (
                <LoadingScreenComponent />
              ) : defaultMenu?.componentName === "GeneralFeature" ? (
                <GeneralFeature injectionModule={GeneralsInjection} />
              ) : defaultMenu?.path ? (
                <Navigate to={defaultMenu.path} replace />
              ) : (
                <NotFound />
              )}
            </PrivateRoute>
          }
        />
        {routeRendering}
        {shouldRedirectToDefaultMenu ? (
          <Route
            path={location?.pathname}
            key={"redirect-to-default-menu"}
            element={
              <PrivateRoute
                path={location?.pathname}
                key={"redirect-to-default-menu"}
              >
                {defaultMenu?.path ? (
                  <Navigate to={defaultMenu.path} replace />
                ) : (
                  <NotFound />
                )}
              </PrivateRoute>
            }
          />
        ) : (
          <></>
        )}
      </>
    );
  };

  useEffect(() => {
    if (!diInjected) {
      initDependency();
    }
  }, []);

  useEffect(() => {
    document.querySelector("html").style.scrollBehavior = "auto";
    window.scroll({ top: 0 });
    document.querySelector("html").style.scrollBehavior = "";
    focusHandling(null);
  }, [location.pathname]);

  if (!diInjected) {
    return <LoadingScreenComponent />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        key={"login-route"}
        element={
          <LoginFeature
            navigate={navigate}
            injectionModule={AuthenticationInjection}
          />
        }
      ></Route>
      <>{pageRendering()}</>
      <Route
        path="/404"
        element={
          <PrivateRoute path="/404">
            <NotFound />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

export default App;
